import React from 'react';
import { Spinner, Text } from '../components/UI';
// Direct file import on purpose: the hooks barrel pulls in services, which pull in this HOC's consumers — a cycle that left the Dashboard with an undefined services module.
import { useIsMobile } from '../hooks/useIsMobile';
import { SalesTabBar, SALES_TAB_BAR_HEIGHT } from '../pages/sales/shared/SalesTabBar.component';
import { MeetingWrapUpQueue } from '../pages/sales/wrapup/MeetingWrapUpQueue.component';
import { useSalesAccess } from '../services/crm';
import { withAuth, WithAuth } from './withAuth.hoc';

/**
 * Page guard for internal sales surfaces.
 *
 * Sales access is a grant in order.sales_reps, not a permission tier, so
 * `withAuth` cannot express it. This composes with withAuth at 'EVERYBODY' —
 * the user must still be logged in and verified — and then requires the grant.
 *
 * The check is cosmetic: every /api/v2/crm route is independently gated by
 * RequireSalesAccess on the server. This only avoids rendering a page that
 * would 403 on every call.
 */
export const withSalesAccess = <P extends object>(
    Component: React.FunctionComponent<P & WithAuth>,
    minLevel: 'rep' | 'manager' = 'rep'
) => {
    // Guarded takes the same P & WithAuth that withAuth injects, so the injected
    // `user` prop passes straight through to the wrapped page.
    const Guarded: React.FunctionComponent<P & WithAuth> = (props: P & WithAuth): React.ReactElement => {
        const [salesAccess, salesFunctions] = useSalesAccess();
        const isMobile = useIsMobile();

        React.useEffect(() => {
            salesFunctions.resolve();
            // resolve is stable via useCallback; re-running on every render would
            // re-fire the request continuously.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        if (salesAccess.loading) {
            return (
                <div className="flex justify-center p-20">
                    <Spinner show={true} />
                </div>
            );
        }

        const permitted = minLevel === 'manager' ? salesAccess.isManager : salesAccess.isRep;

        if (!permitted) {
            return (
                <div className="text-center flex flex-col gap-y-4 p-20">
                    <Text className="!text-3xl"> No Access </Text>
                    <Text>
                        The Sales area is limited to team members with sales access.
                        Ask a sales manager to grant it to your account.
                    </Text>
                </div>
            );
        }

        // Phones get the Sales tab bar under every page, and room at the bottom so nothing hides behind it.
        if (isMobile) {
            return (
                <div style={{ paddingBottom: `calc(${SALES_TAB_BAR_HEIGHT} + env(safe-area-inset-bottom))` }}>
                    <Component {...props} />
                    <SalesTabBar />
                    <MeetingWrapUpQueue />
                </div>
            );
        }
        // The wrap-up queue rides along with every sales page: a finished call keeps asking until it is wrapped up.
        return <><Component {...props} /><MeetingWrapUpQueue /></>;
    };

    return withAuth<P>(Guarded, 'EVERYBODY');
};
